import React, { useMemo } from 'react'
import { AgCartesianChartOptions } from 'ag-charts-enterprise'
import { AgCharts } from 'ag-charts-react'
import { Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import { CustomExchangeRate, DefaultExchangeRate } from '../types/exchangeRate'
import { IndexedDefaultExchangeRates } from '../queries/defaultExchangeRateOptions'
import { categorizeExchangeRates } from './categorizeExchangeRates'

interface ExchangeRateChartProps {
  // categorizedRates: CategorizedExchangeRates
  allRates: CustomExchangeRate[]
  defaultRates?: IndexedDefaultExchangeRates
  dates: Dayjs[]
}

const ExchangeRateAgChart: React.FC<ExchangeRateChartProps> = ({ allRates, defaultRates, dates }) => {
  const { t } = useTranslation()
  // Transformoi kategorisoidut ratet charttia varten
  const categorizedRates = useMemo(() => categorizeExchangeRates(allRates), [allRates])

  const chartData = useMemo(() => {
    return dates.map(date => {
      const monthKey = date.format('MM/YYYY')

      // Hae arvot kaikille kuudelle kategorialle käyttäen indexiä
      const plActualsRate = categorizedRates['pl-actuals'][monthKey]
      const plBudgetRate = categorizedRates['pl-budget'][monthKey]
      const plForecastRate = categorizedRates['pl-forecast'][monthKey]
      const bsActualsRate = categorizedRates['bs-actuals'][monthKey]
      const bsBudgetRate = categorizedRates['bs-budget'][monthKey]
      const bsForecastRate = categorizedRates['bs-forecast'][monthKey]
      const defaultRatePl = defaultRates?.[`pl-${monthKey}`]
      const defaultRateBs = defaultRates?.[`bs-${monthKey}`]

      const parseValue = (rate: CustomExchangeRate | DefaultExchangeRate | undefined | null) => {
        if (!rate || rate.value == null) return null
        return typeof rate.value === 'number' ? rate.value : parseFloat(rate.value.toString())
      }

      return {
        month: monthKey,
        date: date.toDate(),
        'pl-actuals': parseValue(plActualsRate),
        'pl-budget': parseValue(plBudgetRate),
        'pl-forecast': parseValue(plForecastRate),
        'bs-actuals': parseValue(bsActualsRate),
        'bs-budget': parseValue(bsBudgetRate),
        'bs-forecast': parseValue(bsForecastRate),
        DefaultPl: parseValue(defaultRatePl),
        DefaultBs: parseValue(defaultRateBs)
      }
    })
  }, [categorizedRates, defaultRates, dates])

  const chartOptions: AgCartesianChartOptions = {
    data: chartData,
    axes: [
      {
        type: 'time',
        position: 'bottom',
        label: {
          format: '%m/%Y',
          rotation: 45
        }
      },
      {
        type: 'number',
        position: 'left',
        title: {
          text: t('exchangeRatesPage:exchangeRate')
        },
        label: {
          format: '.4f'
        }
      }
    ],
    series: [
      // Default series
      {
        type: 'line',
        xKey: 'date',
        yKey: 'DefaultPl',
        yName: `${t('global:default')} ${t('reportTypes:INCOME_STATEMENT')} (BOF)`,
        stroke: '#ffe604',
        strokeWidth: 2,
        interpolation: {
          type: 'smooth'
        },
        marker: {
          enabled: true,
          shape: 'star',
          size: 6,
          fill: '#ff0000'
        }
      },
      {
        type: 'line',
        xKey: 'date',
        yKey: 'DefaultBs',
        yName: `${t('global:default')} ${t('reportTypes:BALANCE_SHEET')} (BOF)`,
        stroke: '#64ff04',
        strokeWidth: 2,
        interpolation: {
          type: 'smooth'
        },
        marker: {
          enabled: true,
          shape: 'star',
          size: 6,
          fill: '#ff0000'
        }
      },
      // P&L series
      {
        type: 'line',
        xKey: 'date',
        yKey: 'pl-actuals',
        yName: `${t('reportTypes:INCOME_STATEMENT')} ${t('global:actuals')}`,
        stroke: '#2196F3',
        strokeWidth: 2,
        marker: {
          enabled: true,
          shape: 'circle',
          size: 6
        }
      },
      {
        type: 'line',
        xKey: 'date',
        yKey: 'pl-budget',
        yName: `${t('reportTypes:INCOME_STATEMENT')} ${t('global:budget')}`,
        stroke: '#1976D2',
        strokeWidth: 2,
        lineDash: [5, 5],
        marker: {
          enabled: true,
          shape: 'diamond',
          size: 6
        }
      },
      {
        type: 'line',
        xKey: 'date',
        yKey: 'pl-forecast',
        yName: `${t('reportTypes:INCOME_STATEMENT')} ${t('global:forecast')}`,
        stroke: '#0D47A1',
        strokeWidth: 2,
        lineDash: [2, 2],
        marker: {
          enabled: true,
          shape: 'triangle',
          size: 6
        }
      },
      // Balance Sheet series
      {
        type: 'line',
        xKey: 'date',
        yKey: 'bs-actuals',
        yName: `${t('reportTypes:BALANCE_SHEET')} ${t('global:actuals')}`,
        stroke: '#FF9800',
        strokeWidth: 2,
        marker: {
          enabled: true,
          shape: 'square',
          size: 6
        }
      },
      {
        type: 'line',
        xKey: 'date',
        yKey: 'bs-budget',
        yName: `${t('reportTypes:BALANCE_SHEET')} ${t('global:budget')}`,
        stroke: '#F57C00',
        strokeWidth: 2,
        lineDash: [5, 5],
        marker: {
          enabled: true,
          shape: 'cross',
          size: 6
        }
      },
      {
        type: 'line',
        xKey: 'date',
        yKey: 'bs-forecast',
        yName: `${t('reportTypes:BALANCE_SHEET')} ${t('global:forecast')}`,
        stroke: '#E65100',
        strokeWidth: 2,
        lineDash: [2, 2],
        marker: {
          enabled: true,
          shape: 'plus',
          size: 6
        }
      }
    ],
    legend: {
      position: 'bottom',
      spacing: 40,
      item: {
        marker: {
          strokeWidth: 0
        }
      }
    },
    tooltip: {
      enabled: true,
      mode: 'shared',
      showArrow: true
    }
  }

  return (
    <div style={{ width: '100%', height: 500 }}>
      <AgCharts options={chartOptions} />
    </div>
  )
}

export default ExchangeRateAgChart
